import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import moment from 'moment';
import toast from 'react-hot-toast';
import {AiFillDelete} from 'react-icons/ai';
import {IoMdEye} from 'react-icons/io';
import {NavLink} from 'react-router-dom';

import {
  useGetCandidatesQuery,
  useRemoveCandidateFromJobMutation,
} from '../api/candidate';
import CustomTable from '../components/common/CustomTable';
import {SelectFieldHookForm} from '../components/ui/SelectField';
import {DebounceInput} from '../components/ui/InputField';
import {pageRoutes} from '../routes';
import {jobStatus, stageOptions} from '../utils/constants';
import {invalidateQueryV2} from '../services/reactQuery';
import AlertModal from '../components/AlertModal';

const ManageCandidate = () => {
  const [formData, setFormData] = useState({
    job: null,
    stage: null,
    search: '',
    sort: null,
    sortBy: null,
    page: 1,
    limit: 10,
    isDeleted: false,
  });
  const [removeCandidateId, setRemoveCandidateId] = useState(null);

  const removeCandidateFromJobMutation = useRemoveCandidateFromJobMutation();
  const {isLoading: isRemoveCandidateLoading} = removeCandidateFromJobMutation;

  const {
    data: candidatesData,
    isFetching: isCandidateLoading,
    error,
  } = useGetCandidatesQuery(formData);
  const {
    candidateData: allCandidates,
    totalItems = null,
    totalPages = null,
  } = candidatesData?.data || {candidateData: []};

  const {handleSubmit, control, watch} = useForm({
    mode: 'all',
  });

  const handleFormSubmission = async (values) => {
    const {search, job, stage} = values;
    let payload = {search};

    if (job?.value) payload = {...payload, job: job.value};
    if (stage?.value) payload = {...payload, stage: stage.value};

    // set pagination to page 1
    setFormData((prev) => ({
      ...prev,
      ...payload,
      page: 1,
    }));
  };

  const handleDeleteUser = (userId) => {
    removeCandidateFromJobMutation.mutate(userId, {
      onSuccess: () => {
        setRemoveCandidateId(null);
        toast.success('Employer successfully removed.');
        invalidateQueryV2(['employers']);
      },
      onError: (data) => toast.error(data.response.data.message),
    });
  };

  useEffect(() => {
    const subscription = watch(handleSubmit(handleFormSubmission));
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const handleSort = (sortingDetails) => {
    const {page = null, sortBy, sortDirection = null} = sortingDetails;
    setFormData((prevFormData) => ({
      ...prevFormData,
      page: page || prevFormData.page,
      sortBy,
      sort: sortDirection,
    }));
  };

  // table has no data, change the page
  useEffect(() => {
    if (!isCandidateLoading && !allCandidates.length && !error) {
      setFormData((prev) => ({
        ...prev,
        page: prev.page === 1 ? 1 : prev.page - 1,
      }));
    }
  }, [isCandidateLoading, allCandidates, error]);

  const tHeadData = [
    {
      id: 2,
      keyIndex: 'title',
      label: 'Job Title',
      sort: true,
      width: '30%',
    },
    {
      id: 3,
      keyIndex: 'firstName',
      label: 'Candidate Name',
      sort: true,
      render: (_, {firstName, lastName}) => `${firstName} ${lastName}`,
      width: '20%',
    },
    {
      id: 4,
      keyIndex: 'stage',
      label: 'Stage',
      sort: true,
      width: '20%',
      render: (_, {stage, accepted, declined}) => {
        if (declined) {
          return 'Declined';
        } else if (!accepted && !declined) {
          return 'Pending';
        }
        return jobStatus[stage];
      },
    },
    {
      id: 6,
      keyIndex: 'applicationDate',
      label: 'Applied Time',
      sort: true,
      width: '20%',
      render: (item) => <>{moment(item).format('MM/DD/YYYY h:mm a')}</>,
    },
    {
      id: 7,
      keyIndex: 'lastName',
      label: 'Action',
      width: '10%',
      render: (_, {_id}) => (
        <div className="flex gap-2">
          <NavLink
            aria-label="show candidat details"
            to={`${pageRoutes.candidate}/${_id}`}
          >
            <IoMdEye size={23} />
          </NavLink>
          <button
            type="button"
            aria-label="remove candidate from job"
            onClick={() => setRemoveCandidateId(_id)}
          >
            <AiFillDelete size={20} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <AlertModal
        type="warning"
        isOpen={removeCandidateId}
        onClose={() => setRemoveCandidateId(null)}
        title="Confirm this action"
        onCancel={() => setRemoveCandidateId(null)}
        onOK={() => handleDeleteUser(removeCandidateId)}
        okLoading={isRemoveCandidateLoading}
      >
        Are you sure you want to delete this candidate?
      </AlertModal>

      <div className="px-8 py-6">
        <div className="flex items-center gap-2">
          <h3 className="text-2xl font-bold">
            Candidates {!!Number(totalItems) && `(${totalItems})`}
          </h3>
        </div>
        <form className="py-5">
          <div className="flex gap-5">
            <DebounceInput
              control={control}
              name="search"
              placeholder="Search"
            />
            <div className="flex gap-4">
              <SelectFieldHookForm
                placeholder="Stage"
                control={control}
                name="stage"
                options={stageOptions}
              />
            </div>
          </div>
        </form>

        <div className="bg-[white] p-10 border">
          <CustomTable
            tHeadData={tHeadData}
            tBodyData={allCandidates}
            handleSort={handleSort}
            pageCount={totalPages}
            currentPage={formData.page}
            loading={isCandidateLoading}
          />
        </div>
      </div>
    </>
  );
};

export default ManageCandidate;
